import { Component, OnInit, Input, ChangeDetectionStrategy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UISearchZoneModel, UiSearchDefinitionService } from 'e5core-angular-services';
import { SearchFormModel } from '../../models/search-form.model';
import { FieldDataType } from '../../enums/field-data-type';
import { DataSourceQuery, QueryExpression } from '../../datasource/data-source-query';
import { UiStyleService } from '../../services/ui-style.service';
import { SearchDeliveryService } from '../../services/search-delivery.service';

@Component({
	selector: 'e5core-ui-search-form',
	styleUrls: ['./../../css/default.css'],
	template: `
  <span *ngIf="searchForm$" [ngSwitch]="style.current()">
    <e5core-ui-search-form-box *ngSwitchCase='0' [searchForm$]='searchForm$' [zones]='zones' [zoneTemplateId]='zoneTemplateId'></e5core-ui-search-form-box>
    <e5core-ui-search-form-bootstrap *ngSwitchCase='1' [searchForm$]='searchForm$' [zones]='zones' [zoneTemplateId]='zoneTemplateId'></e5core-ui-search-form-bootstrap>
    <e5core-ui-search-form-material *ngSwitchCase='2' [searchForm$]='searchForm$' [zones]='zones' [zoneTemplateId]='zoneTemplateId'></e5core-ui-search-form-material>
  </span>
  <div *ngIf="!searchForm$" class="e5core-component-error">&lt;e5core-ui-search-form&gt; [searchForm$] must have a BehaviorSubject&lt;SearchModel&gt;</div>
  <div *ngIf="errorMessage" class="e5core-component-error">&lt;e5core-ui-search-form&gt; error : {{errorMessage}}</div>`,
	//changeDetection: ChangeDetectionStrategy.OnPush
})
export class E5coreUiSearchFormComponent implements OnInit {

	@Input() searchForm$: BehaviorSubject<SearchFormModel>;
	@Input() searchDefinitionId: number;
	@Input() defaultValues: { [s: string]: string; };
	@Output() queryChange = new EventEmitter<DataSourceQuery>()

	zones: UISearchZoneModel[];
	errorMessage: string;
	zoneTemplateId: string;

	constructor(
		public style: UiStyleService, 
		private searchDeliveryService: SearchDeliveryService, 
		public search:UiSearchDefinitionService,
		private cd: ChangeDetectorRef) {
	}

	ngOnInit() {
		if (this.searchForm$ == null) {
			console.error('e5core-ui-search-form [searchForm$] must have a BehaviorSubject<SearchFormModel>');
			return;
		}
		if (this.searchDefinitionId) {
			this.searchDeliveryService.getSearchDefinition(this.searchForm$, this.searchDefinitionId).subscribe(
				s => {
					this.applyDefaultValues(this.defaultValues);
				},
				error => {
					this.errorMessage = error.message || "Unable to get data from server.";
					console.error(error);
					this.cd.markForCheck();
				}
			);
		} else {
			console.warn("e5core-ui-search-form searchId is missing. Expecting one later...");
		}

		this.searchForm$.subscribe(
			searchModel => {
				this.errorMessage = "";
				this.zones = (searchModel) ? searchModel.zones : undefined;
				this.zoneTemplateId = (searchModel && searchModel.zoneTemplateId) ? searchModel.zoneTemplateId.toUpperCase() : "";
				this.cd.markForCheck();
				this.queryChange.emit(this.createDataSourceQuery(searchModel));
			}
		)
	}

	private createDataSourceQuery(model: SearchFormModel): DataSourceQuery {
		let query: DataSourceQuery = null;
		if (model && model.properties && model.properties.length > 0) {
			query = {
				Expressions: model.properties
					.filter(p => p.value)
					.map<QueryExpression>(p => <QueryExpression>{
						Field: p.propertyId,
						Value: p.value
					})
			}
		}
		return (query);
	}

	/**
	 * If there are any default values on the form, then set them.
	 * @param values the default values to use on the form
	 */
	private applyDefaultValues(values: { [s: string]: string; }, reset?: boolean) {
		let searchForm = <SearchFormModel>{ ...this.searchForm$.getValue() };
		let updated = false;
		if (reset) {
			searchForm.properties.forEach(p => {
				p.value = undefined;
				p.resolvedValue = undefined;
			})
			updated = true;
		}
		if (values) {
			if (searchForm.properties) {
				Object.keys(values).forEach(key => {
					let property = searchForm.properties.find(p => p.propertyId == key)
					if (property) {
						updated = true;
						let value = values[key]
						property.value = value; // will be set to undefined if not specified
						if (property.fieldDataType != FieldDataType.List && property.fieldDataType != FieldDataType.Lookup) // if it's not a list or lookup
							property.resolvedValue = value;
					}
				});
			}
		}
		if (updated)
			this.searchForm$.next(searchForm);
	}

	/**
	 * Reset the serch form to the original default values, or clear it.
	 */
	public reset() {
		this.applyDefaultValues(this.defaultValues, true /* reset */);
		this.cd.markForCheck();
	}
}
